import { isoDateValue } from './trade-invoice-payload';
import { lineTotal } from './adjustment-note-schema';
import type { AdjustmentLineValues, AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import type { InvoiceRow } from '../types';

export interface SaveAdjustmentContext {
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly noteType: 'Credit' | 'Debit';
  readonly existingInvoice: InvoiceRow | null;
  readonly aiExtractionId?: string;
}

interface AdjustmentWirePayload extends Record<string, unknown> {
  invoice_number: string;
  currency: 'USD' | 'EUR';
  total_amount: number;
  vat_amount: number;
  original_due_date: string;
}

// A note keeps the direction of the side it's raised on (the parent's column); the sign is carried on
// the amount, not by flipping the side. The backend re-asserts this against the parent invoice.
export function noteDirection(side: 'buy' | 'sell'): 'Purchase' | 'Sale' {
  return side === 'buy' ? 'Purchase' : 'Sale';
}

// Debit notes carry free-text payment terms which Launchpad prepends to the
// remarks (there is no dedicated column); Credit notes pass remarks verbatim.
function composeRemarks(
  values: AdjustmentNoteSubmitValues,
  noteType: 'Credit' | 'Debit',
): string | undefined {
  if (noteType !== 'Debit' || values.payment_terms === undefined) return values.remarks;
  const prefix = `Payment Terms: ${values.payment_terms}`;
  return values.remarks !== undefined ? `${prefix}\n${values.remarks}` : prefix;
}

function adjustmentWireBase(
  values: AdjustmentNoteSubmitValues,
  side: 'buy' | 'sell',
  noteType: 'Credit' | 'Debit',
): AdjustmentWirePayload {
  const out: AdjustmentWirePayload = {
    invoice_number: values.invoice_number,
    currency: values.currency,
    total_amount: values.total_amount,
    vat_amount: values.vat_amount,
    original_due_date: isoDateValue(values.original_due_date),
  };
  if (values.issue_date !== undefined) out['issue_date'] = values.issue_date;
  if (values.note_reason !== undefined) out['note_reason'] = values.note_reason;
  if (values.settlement_method !== undefined) out['settlement_method'] = values.settlement_method;
  if (values.company_name !== undefined) out['company_name'] = values.company_name;
  if (side === 'buy' && values.issuer_company_name !== undefined) {
    out['issuer_company_name'] = values.issuer_company_name;
  }
  const remarks = composeRemarks(values, noteType);
  if (remarks !== undefined) out['remarks'] = remarks;
  return out;
}

// Both sell-side notes itemise the charge (Debit demurrage, Credit categories);
// the buy side carries a single total.
function isItemised(ctx: SaveAdjustmentContext): boolean {
  return ctx.side === 'sell';
}

// line_total is derived (quantity × unit_price), recomputed here so the wire
// value always matches the displayed total. `type` is dropped when blank so the
// backend's optional-but-non-empty rule accepts demurrage (typeless) lines.
function toWireLine(line: AdjustmentLineValues): Record<string, unknown> {
  const out: Record<string, unknown> = {
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unit_price: line.unit_price,
    line_total: lineTotal(line.quantity, line.unit_price),
  };
  if (line.type !== undefined && line.type.trim() !== '') out['type'] = line.type;
  return out;
}

export function buildAdjustmentNoteSavePayload(
  values: AdjustmentNoteSubmitValues,
  ctx: SaveAdjustmentContext,
): Record<string, unknown> {
  const base = adjustmentWireBase(values, ctx.side, ctx.noteType);
  if (isItemised(ctx) && values.adjustment_lines !== undefined) {
    base['adjustment_lines'] = values.adjustment_lines.map(toWireLine);
    // The sell-side total is the sum of its lines, never hand-entered.
    base.total_amount = adjustmentLinesSum(values.adjustment_lines);
  }
  if (ctx.existingInvoice !== null) return base;
  const out: Record<string, unknown> = {
    bl_order_id: ctx.blOrderId,
    order_id: ctx.orderId,
    invoice_direction: noteDirection(ctx.side),
    invoice_type: 'Adjustment',
    note_type: ctx.noteType,
    adjusts_invoice_id: values.adjusts_invoice_id,
    ...base,
  };
  // A declared price/weight change rides the create so the backend recomputes
  // the amount, enforces the credit-vs-debit sign, and moves the core values.
  if (
    (values.change_driver === 'price' || values.change_driver === 'weight') &&
    values.change_new_value !== undefined
  ) {
    out['value_change'] = { driver: values.change_driver, new_value: values.change_new_value };
  }
  if (ctx.aiExtractionId !== undefined) out['ai_extraction_id'] = ctx.aiExtractionId;
  return out;
}

export function adjustmentLinesSum(lines: AdjustmentLineValues[] | undefined): number {
  if (lines === undefined) return 0;
  // Derived from quantity × unit_price (line_total is read-only in the form).
  return lines.reduce((sum, line) => sum + lineTotal(line.quantity, line.unit_price), 0);
}
